import React, { useEffect, useMemo, useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArchive, faEllipsisV, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import Userfront from '../../auth/userfrontCompat';
import api from '../../api/axios';
import { useNotification } from '../../components/NotificationContext';
import NewCampaignModal from './NewCampaignModal';

const STATUS_META = {
  Concept: { label: 'Setup', tone: 'bg-slate-100 text-slate-700', description: 'Nog niet gestart' },
  Activating: { label: 'Starten…', tone: 'bg-blue-50 text-blue-700', description: 'Queue wordt opgebouwd' },
  Active: { label: 'Actief', tone: 'bg-emerald-50 text-emerald-700', description: 'Outreach loopt' },
  'Needs attention': { label: 'Actie nodig', tone: 'bg-rose-50 text-rose-700', description: 'Menselijke controle vereist' },
  Paused: { label: 'Gepauzeerd', tone: 'bg-amber-50 text-amber-700', description: 'Tijdelijk stilgezet' },
  Completed: { label: 'Voltooid', tone: 'bg-indigo-50 text-indigo-700', description: 'Geen open werk meer' },
  Stopped: { label: 'Gestopt', tone: 'bg-rose-50 text-rose-700', description: 'Definitief gestopt' },
  Archived: { label: 'Gearchiveerd', tone: 'bg-gray-100 text-gray-500', description: 'Verborgen uit standaardweergave' },
};

const HEALTH_META = {
  healthy: { label: 'Gezond', tone: 'bg-emerald-50 text-emerald-700 ring-emerald-200' },
  waiting: { label: 'Wachtend', tone: 'bg-sky-50 text-sky-700 ring-sky-200' },
  attention: { label: 'Aandacht nodig', tone: 'bg-amber-50 text-amber-800 ring-amber-200' },
  blocked: { label: 'Geblokkeerd', tone: 'bg-rose-50 text-rose-700 ring-rose-200' },
};

const LIVE_REFRESH_STATUSES = new Set(['Active', 'Activating']);
const LIVE_REFRESH_MS = 30_000;

function pct(value, total) {
  return total ? Math.round((value / total) * 100) : 0;
}

function legacyMetrics(campaign) {
  const total = Number(campaign.leadCount || 0);
  const accepted = Number(campaign.newConnectionCount || 0);
  const replied = Number(campaign.repliedCount || 0);
  return {
    total,
    acceptance: pct(accepted, total),
    reply: pct(replied, accepted || total),
  };
}

function setupProgress(campaign) {
  const hasAudience = Boolean(campaign.leadListId || campaign.leadAcquisition?.status === 'ready');
  const mode = campaign.campaignMode || 'agent';
  const hasPlaybook = mode === 'agent'
    ? Boolean(campaign.agentPolicy?.goal || campaign.campaignContext || campaign.agentPolicy?.idealCustomerProfile)
    : Number(campaign.workflowNodeCount || 0) > 0;
  const hasSchedule = Boolean(campaign.timezone && campaign.schedule);
  const done = [hasAudience, hasPlaybook, hasSchedule].filter(Boolean).length;
  return { done, total: 3, percent: Math.round((done / 3) * 100) };
}

function sourceLabel(campaign) {
  const raw = String(campaign.leadAcquisition?.provider || campaign.leadAcquisition?.source || '').toLowerCase();
  if (raw.includes('lusha')) return 'Lusha';
  if (raw.includes('sales')) return 'Sales Navigator';
  if (raw.includes('hubspot')) return 'HubSpot';
  if (raw.includes('new')) return 'Nieuwe leads';
  return campaign.leadListId ? 'Bestaande lijst' : 'Nog kiezen';
}

function formatNext(summary) {
  const next = summary?.nextAction;
  if (!next?.label) return null;
  if (!next.at) return next.label;
  const date = new Date(next.at);
  if (Number.isNaN(date.getTime())) return next.label;
  const diff = date.getTime() - Date.now();
  if (diff > 0 && diff < 60 * 60 * 1000) return `${next.label} · over ${Math.max(1, Math.round(diff / 60000))} min`;
  return `${next.label} · ${date.toLocaleString('nl-NL', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}`;
}

function formatRefreshTime(value) {
  if (!value) return 'nog niet bijgewerkt';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'zojuist bijgewerkt';
  return `bijgewerkt om ${date.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`;
}

function CampaignCard({ campaign, summary, currentUser, onArchive, onDelete }) {
  const status = STATUS_META[campaign.status] || { label: campaign.status || 'Onbekend', tone: 'bg-gray-100 text-gray-600', description: '' };
  const healthMeta = summary?.health ? (HEALTH_META[summary.health.status] || HEALTH_META.waiting) : null;
  const metrics = legacyMetrics(campaign);
  const setup = setupProgress(campaign);
  const isConcept = campaign.status === 'Concept';
  const nextLabel = formatNext(summary);
  const outcomes = summary?.outcomes;
  const primaryLabel = isConcept
    ? 'Setup vervolgen'
    : campaign.status === 'Needs attention'
      ? 'Probleem oplossen'
      : campaign.status === 'Paused'
        ? 'Bekijken & hervatten'
        : ['Active', 'Activating'].includes(campaign.status)
          ? 'Monitoren'
          : 'Resultaten bekijken';
  const primaryTone = summary?.health?.status === 'blocked'
    ? 'bg-rose-600 hover:bg-rose-700'
    : summary?.health?.status === 'attention'
      ? 'bg-amber-600 hover:bg-amber-700'
      : 'bg-blue-600 hover:bg-blue-700';
  const canManage = String(campaign.user || '').toLowerCase() === currentUser || String(Userfront.user?.data?.role || '').toLowerCase() === 'admin';

  return (
    <article className={`rounded-2xl border bg-white p-5 shadow-sm transition hover:shadow-md ${summary?.health?.status === 'blocked' ? 'border-rose-200' : summary?.health?.status === 'attention' ? 'border-amber-200' : 'hover:border-gray-300'}`}>
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Link to={`/campaigns/${campaign._id}`} className="truncate text-base font-semibold text-gray-950 hover:text-blue-700">{campaign.title || 'Campagne'}</Link>
            <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${status.tone}`}>{status.label}</span>
            {healthMeta && <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset ${healthMeta.tone}`}>{healthMeta.label}</span>}
            <span className="rounded-full bg-gray-50 px-2.5 py-1 text-xs text-gray-600">{campaign.campaignMode === 'manual' ? 'Manueel' : 'Agent'}</span>
            <span className="rounded-full bg-gray-50 px-2.5 py-1 text-xs text-gray-600">{sourceLabel(campaign)}</span>
          </div>
          <p className="mt-1 truncate text-sm text-gray-500">{campaign.description || campaign.campaignContext || status.description}</p>
          {isConcept ? (
            <div className="mt-3 max-w-md">
              <div className="mb-1 flex justify-between text-xs text-gray-500"><span>Setup {setup.done}/{setup.total}</span><span>{setup.percent}%</span></div>
              <div className="h-1.5 overflow-hidden rounded-full bg-gray-100"><div className="h-full rounded-full bg-blue-600" style={{ width: `${setup.percent}%` }} /></div>
            </div>
          ) : nextLabel ? (
            <div className="mt-3 rounded-lg bg-blue-50 px-3 py-2 text-xs text-blue-900">
              <span className="font-semibold">Hierna:</span> {nextLabel}
              {summary?.attentionCount > 0 && <span className="ml-2 font-semibold text-amber-800">· {summary.attentionCount} aandachtspunt{summary.attentionCount === 1 ? '' : 'en'}</span>}
            </div>
          ) : null}
        </div>

        <div className="grid grid-cols-3 gap-3 text-center sm:min-w-[300px]">
          <div><strong className="block text-lg text-gray-950">{metrics.total}</strong><span className="text-[11px] text-gray-400">Leads</span></div>
          <div><strong className="block text-lg text-blue-700">{outcomes ? outcomes.positiveReplies : `${metrics.reply}%`}</strong><span className="text-[11px] text-gray-400">{outcomes ? 'Positieve replies' : 'Replies'}</span></div>
          <div><strong className="block text-lg text-violet-700">{outcomes ? outcomes.meetings : `${metrics.acceptance}%`}</strong><span className="text-[11px] text-gray-400">{outcomes ? 'Meetings' : 'Acceptatie'}</span></div>
        </div>

        <div className="flex items-center justify-end gap-2">
          <Link to={`/campaigns/${campaign._id}`} className={`rounded-lg px-3.5 py-2 text-sm font-semibold text-white ${primaryTone}`}>{primaryLabel}</Link>
          <Dropdown align="end">
            <Dropdown.Toggle as="button" className="rounded-lg border bg-white px-3 py-2 text-gray-500"><FontAwesomeIcon icon={faEllipsisV} /></Dropdown.Toggle>
            <Dropdown.Menu>
              {!['Active', 'Activating'].includes(campaign.status) && canManage && <>
                <Dropdown.Item onClick={() => onArchive(campaign._id)}><FontAwesomeIcon icon={faArchive} className="mr-2" />Archiveren</Dropdown.Item>
                <Dropdown.Item onClick={() => onDelete(campaign._id)} className="text-danger"><FontAwesomeIcon icon={faTrash} className="mr-2" />Verwijderen</Dropdown.Item>
              </>}
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>
    </article>
  );
}

export default function Campaigns() {
  const { t, i18n } = useTranslation();
  const { showNotification, removeNotification } = useNotification();
  const [campaigns, setCampaigns] = useState([]);
  const [operations, setOperations] = useState({});
  const [operationsUpdatedAt, setOperationsUpdatedAt] = useState(null);
  const [operationsRefreshing, setOperationsRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [healthFilter, setHealthFilter] = useState('all');
  const [ownerFilter, setOwnerFilter] = useState('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteCampaignId, setDeleteCampaignId] = useState(null);
  const currentUser = String(Userfront.user?.email || '').toLowerCase();

  const notify = (message, type = 'success') => {
    const id = showNotification(message, type);
    setTimeout(() => removeNotification(id), 4000);
  };

  const loadOperations = async ({ silent = false } = {}) => {
    if (!silent) setOperationsRefreshing(true);
    try {
      const response = await api.get('/campaigns/organization/operations-overview?limit=30');
      const summaries = response.data?.summaries || {};
      setOperations(summaries);
      setOperationsUpdatedAt(response.data?.generatedAt || new Date().toISOString());
      setCampaigns((current) => current.map((campaign) => {
        const lifecycle = summaries[String(campaign._id)]?.lifecycle;
        return lifecycle && lifecycle !== campaign.status ? { ...campaign, status: lifecycle } : campaign;
      }));
    } finally {
      if (!silent) setOperationsRefreshing(false);
    }
  };

  const loadCampaigns = async () => {
    try {
      const response = await api.get('/campaigns/organization/cards-overview?limit=200');
      const items = Array.isArray(response.data?.campaigns) ? response.data.campaigns : [];
      setCampaigns(items);
      loadOperations().catch((error) => console.warn('Campaign operations overview unavailable:', error.message));
    } catch (error) {
      if (error.response?.status === 404) {
        setCampaigns([]);
        setOperations({});
      } else {
        notify(error.response?.data?.message || 'Campagnes konden niet worden geladen.', 'danger');
      }
    }
  };

  useEffect(() => {
    loadCampaigns();
    api.get('/user/user').then((response) => {
      const language = response.data?.language || response.data?.user?.language;
      if (language) i18n.changeLanguage(language);
    }).catch(() => {});
  }, []);

  const hasLiveCampaigns = useMemo(() => campaigns.some((campaign) => LIVE_REFRESH_STATUSES.has(campaign.status)), [campaigns]);

  useEffect(() => {
    if (!hasLiveCampaigns) return undefined;
    const refreshVisibleOverview = () => {
      if (document.visibilityState !== 'visible') return;
      loadOperations({ silent: true }).catch((error) => console.warn('Live campaign overview refresh unavailable:', error.message));
    };
    const timer = window.setInterval(refreshVisibleOverview, LIVE_REFRESH_MS);
    document.addEventListener('visibilitychange', refreshVisibleOverview);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', refreshVisibleOverview);
    };
  }, [hasLiveCampaigns]);

  const owners = useMemo(() => [...new Set(campaigns.map((campaign) => campaign.user).filter(Boolean))], [campaigns]);
  const attentionCount = useMemo(() => campaigns.filter((campaign) => {
    const health = operations[String(campaign._id)]?.health?.status;
    return ['attention', 'blocked'].includes(health) || campaign.status === 'Needs attention';
  }).length, [campaigns, operations]);
  const counts = useMemo(() => ({
    active: campaigns.filter((campaign) => ['Active', 'Activating'].includes(campaign.status)).length,
    setup: campaigns.filter((campaign) => campaign.status === 'Concept').length,
    paused: campaigns.filter((campaign) => campaign.status === 'Paused').length,
    attention: attentionCount,
  }), [campaigns, attentionCount]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return campaigns.filter((campaign) => {
      const summary = operations[String(campaign._id)];
      if (statusFilter === 'all' && campaign.status === 'Archived') return false;
      if (statusFilter !== 'all' && campaign.status !== statusFilter) return false;
      if (healthFilter !== 'all' && summary?.health?.status !== healthFilter) return false;
      if (ownerFilter !== 'all' && campaign.user !== ownerFilter) return false;
      if (!query) return true;
      return `${campaign.title || ''} ${campaign.description || ''} ${campaign.campaignContext || ''} ${summary?.nextAction?.label || ''}`.toLowerCase().includes(query);
    }).sort((a, b) => {
      const aSeverity = operations[String(a._id)]?.health?.severity || 0;
      const bSeverity = operations[String(b._id)]?.health?.severity || 0;
      if (aSeverity !== bSeverity) return bSeverity - aSeverity;
      return new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0);
    });
  }, [campaigns, operations, search, statusFilter, healthFilter, ownerFilter]);

  const createCampaign = async (form) => {
    const response = await api.post('/campaigns/create-v2', form);
    const campaignId = response.data?.campaignId || response.data?.campaign?._id;
    if (!campaignId) throw new Error('Campagne is aangemaakt maar het campagne-id ontbreekt.');
    if (form.contactSource === 'new_contacts') {
      const availableSources = form.sourcingProvider === 'auto' ? ['lusha', 'sales_navigator'] : [form.sourcingProvider];
      try {
        await api.post('/leads/sourcing-briefs/generate', { campaignId, goal: form.context || form.description || form.title, desiredQuantity: 50, availableSources });
      } catch (error) {
        console.warn('Initial sourcing brief could not be generated:', error?.response?.data || error.message);
      }
    }
    setCreateOpen(false);
    window.location.assign(form.contactSource === 'new_contacts' ? `/campaigns/${campaignId}/sourcing` : `/campaigns/${campaignId}`);
  };

  const archiveCampaign = async (id) => {
    try {
      await api.post(`/campaigns/archive/${id}`);
      notify('Campagne gearchiveerd.');
      await loadCampaigns();
    } catch (error) {
      notify(error.response?.data?.message || 'Archiveren mislukt.', 'danger');
    }
  };

  const deleteCampaign = async () => {
    if (!deleteCampaignId) return;
    try {
      await api.delete(`/campaigns/${deleteCampaignId}`);
      notify('Campagne verwijderd.');
      setDeleteCampaignId(null);
      await loadCampaigns();
    } catch (error) {
      notify(error.response?.data?.message || 'Verwijderen mislukt.', 'danger');
    }
  };

  return (
    <main className="min-h-screen space-y-6 bg-gray-50 p-4 sm:p-6 lg:p-8">
      <section className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[.18em] text-blue-600">Outreach</p>
          <h1 className="mt-1 text-2xl font-bold text-gray-950">{t('campaigns.title')}</h1>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">Beheer op uitzonderingen: zie lifecycle, health en de volgende actie zonder iedere campagne te openen.</p>
        </div>
        <button onClick={() => setCreateOpen(true)} className="h-10 rounded-lg bg-blue-600 px-4 text-sm font-semibold text-white shadow-sm hover:bg-blue-700"><FontAwesomeIcon icon={faPlus} className="mr-2" />Nieuwe campagne</button>
      </section>

      <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {[['Actief', counts.active, 'Outreach loopt of start'], ['Setup', counts.setup, 'Nog configureren'], ['Gepauzeerd', counts.paused, 'Wacht op hervatten'], ['Aandacht nodig', counts.attention, 'Controle of blokkade']].map(([label, value, note]) => (
          <div key={label} className={`rounded-2xl border bg-white p-4 ${label === 'Aandacht nodig' && value > 0 ? 'border-amber-200' : ''}`}>
            <span className="text-xs font-medium text-gray-500">{label}</span>
            <strong className="mt-1 block text-2xl text-gray-950">{value}</strong>
            <span className="text-xs text-gray-400">{note}</span>
          </div>
        ))}
      </section>

      <section className="rounded-2xl border bg-white p-3">
        <div className="flex flex-col gap-2 lg:flex-row">
          <input type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Zoek campagne, doel, context of volgende actie…" className="h-10 min-w-0 flex-1 rounded-lg border px-3 text-sm" />
          <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} className="h-10 rounded-lg border bg-white px-3 text-sm"><option value="all">Alle lifecycle-statussen</option>{Object.keys(STATUS_META).map((status) => <option key={status} value={status}>{STATUS_META[status].label}</option>)}</select>
          <select value={healthFilter} onChange={(event) => setHealthFilter(event.target.value)} className="h-10 rounded-lg border bg-white px-3 text-sm"><option value="all">Alle health-statussen</option>{Object.entries(HEALTH_META).map(([value, meta]) => <option key={value} value={value}>{meta.label}</option>)}</select>
          {owners.length > 1 && <select value={ownerFilter} onChange={(event) => setOwnerFilter(event.target.value)} className="h-10 rounded-lg border bg-white px-3 text-sm"><option value="all">Alle eigenaren</option>{owners.map((owner) => <option key={owner}>{owner}</option>)}</select>}
          <button type="button" disabled={operationsRefreshing} onClick={() => loadOperations().catch((error) => notify(error.response?.data?.message || 'Status vernieuwen mislukt.', 'danger'))} className="h-10 rounded-lg border bg-white px-3 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50">{operationsRefreshing ? 'Vernieuwen…' : 'Vernieuwen'}</button>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-gray-400">
          <span className={`h-2 w-2 rounded-full ${hasLiveCampaigns ? 'bg-emerald-500' : 'bg-gray-300'}`} />
          <span>{hasLiveCampaigns ? 'Live monitoring actief · alleen zolang actieve campagnes lopen en dit tabblad zichtbaar is' : 'Live monitoring in rust · geen actieve campagnes'}</span>
          <span>· {formatRefreshTime(operationsUpdatedAt)}</span>
        </div>
      </section>

      <section className="space-y-3">
        {filtered.map((campaign) => <CampaignCard key={campaign._id} campaign={campaign} summary={operations[String(campaign._id)]} currentUser={currentUser} onArchive={archiveCampaign} onDelete={setDeleteCampaignId} />)}
        {!filtered.length && <div className="rounded-2xl border border-dashed bg-white p-10 text-center"><strong className="text-gray-800">Geen campagnes gevonden</strong><p className="mt-1 text-sm text-gray-500">Pas lifecycle/health-filters aan of start een nieuwe campagne.</p></div>}
      </section>

      <NewCampaignModal open={createOpen} onClose={() => setCreateOpen(false)} onCreate={createCampaign} />
      {deleteCampaignId && <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/35 p-4"><div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl"><h3 className="text-lg font-semibold">Campagne verwijderen?</h3><p className="mt-2 text-sm text-gray-600">Dit verwijdert de campagne en bijbehorende campagnegegevens. Dit kan niet automatisch worden teruggedraaid.</p><div className="mt-6 flex justify-end gap-2"><button onClick={() => setDeleteCampaignId(null)} className="rounded-lg border px-4 py-2 text-sm">Annuleren</button><button onClick={deleteCampaign} className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white">Verwijderen</button></div></div></div>}
    </main>
  );
}
